<script setup lang="ts">
import { SlideProps } from "~/components/Filters/Utils/Slide.props"

const props = withDefaults(defineProps<SlideProps>(), {
  openFrom: "left",
  fullScreen: true,
  noBackground: false,
  avoidBodyOverflow: false
})

const emit = defineEmits<{ (e: "onClose"): void; (e: "onAfterClose"): void }>()

const dir = {
  left: "slide-left",
  right: "slide-right",
  bottom: "slide-bottom"
}

const { isOpen } = useUIState(props.name)

const { getBodyOverflow, overflowAuto, overflowHidden } =
  useBodyOverflowManager()

const toggleBodyOverflow = (overflowFunction: () => void) => {
  props?.avoidBodyOverflow && overflowFunction
}

const leaveFunction = () => {
  if (getBodyOverflow() === "hidden") {
    overflowAuto()
  }

  emit("onAfterClose")
}
</script>

<template>
  <Transition name="fade">
    <div
      v-if="!fullScreen && isOpen"
      class="slide-overlay fixed inset-0 z-[998] bg-black-main bg-opacity-50"
      @click="emit('onClose')"
    />
  </Transition>
  <Transition :name="dir[openFrom]" @after-leave="leaveFunction">
    <aside
      v-show="isOpen"
      class="slide fixed z-[999] flex grow flex-col p-0 md:hidden"
      :class="{
        'inset-0': fullScreen,
        'inset-x-0 bottom-0': !fullScreen,
        'bg-white': !noBackground
      }"
      @mouseover="avoidBodyOverflow && toggleBodyOverflow(overflowHidden)"
      @mouseout="avoidBodyOverflow && toggleBodyOverflow(overflowAuto)"
    >
      <slot />
    </aside>
  </Transition>
</template>

<style lang="scss" scoped>
.fade-enter-active,
.fade-leave-active {
  transition: opacity 0.5s ease;
}

.fade-enter-from,
.fade-leave-to {
  opacity: 0;
}

.slide-left-enter-active,
.slide-right-enter-active,
.slide-bottom-enter-active,
.slide-left-leave-active,
.slide-right-leave-active,
.slide-bottom-leave-active {
  @apply transition-transform delay-200 duration-300 ease-in;
}

.slide-left-enter-from,
.slide-left-leave-to {
  @apply -translate-x-full;
}

.slide-right-enter-from,
.slide-right-leave-to {
  @apply translate-x-full;
}

.slide-bottom-enter-from,
.slide-bottom-leave-to {
  @apply translate-y-full;
}
</style>
